import Button from '@allesauto/core/button';
import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import { SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { getDaysForLocale, isSpecialCharacter } from '@allesauto/utils/funcs';
import { Phone16 } from '@carbon/icons-react';
import clsx from 'clsx';
import React, { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';

import LocationMap from '../LocationMap/LocationMap';

export interface CustomerDetails {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string | null;
}

export interface DealerOverviewInfoProps {
  dealerDetails: SchemaTypes.Dealer;
  retailOpeningHours: SchemaTypes.OpeningHours[];
  serviceOpeningHours: SchemaTypes.OpeningHours[];
  bgColor?: string;
  customerDetails?: CustomerDetails;
  hasMap?: boolean;
  editDealer?: () => void;
}

const DealerOverviewInfo: FC<
  React.PropsWithChildren<DealerOverviewInfoProps>
> = ({
  retailOpeningHours,
  serviceOpeningHours,
  dealerDetails,
  bgColor = 'bg-white',
}) => {
  const whitelabel = useWhiteLabel();
  const { name, website, contactPerson, location } = dealerDetails || {};

  //TODO add email from contactPerson
  const { phone } = contactPerson || {};

  const { additionalAddress, city, geoCoordinates, houseNumber, street, zip } =
    location || {};

  const { lat, lon } = geoCoordinates || {};

  const intl = useIntl();

  const weekdays = useMemo(() => {
    const keyDays = getDaysForLocale('en-US', 'long'); // keys for the days are always in english
    const localeDays = getDaysForLocale(intl.locale, 'long'); // translations are in the current locale

    // map keys and translations to use the dictionary later
    return keyDays.reduce<Record<string, string>>((days, dayKey, i) => {
      days[dayKey.toUpperCase()] = localeDays[i];

      return days;
    }, {});
  }, [intl]);

  const className = {
    wrapper: clsx(
      'col-end-25 col-start-1 grid grid-cols-24 main-wrapper relative sm:pb-0 md:pb-60px',
      bgColor
    ),
    description:
      'green-area-wrapper grid grid-cols-25 col-start-1 col-end-18  mt-15px pb-30px z-[6] md:mt-0',
    title: 'font-headline pb-40px pt-60px text-grey100 text-headline4',
    schedule: 'grid-cols-2 mb-5px sm:table-row md:grid',
    scheduleTitle: 'font-weight-bold pb-20px pt-40px text-grey100 text-p1',
    openingHours: 'grid col-end-23 col-start-1 text-grey100 md:col-start-1',
    openingHoursSubtitle: 'text-p1 font-weight-bold',
    retailOpeningHours: 'col-end-11 col-start-1',
    serviceOpeningHours: 'col-end-23 col-start-12',
    workdays: 'pb-5px sm:pr-20px sm:table-cell xl:pr-25px',
    openingHoursRange: 'whitespace-nowrap sm:pb-[9px] sm:table-cell md:pb-5px',
    weekdays: 'pb-5px sm:pr-20px sm:table-cell xl:pr-25px',
    address: 'col-end-23 col-start-1',
    contactPersonContent: 'md:grid lg:grid-cols-24 lg:col-start-1',
    phoneContent:
      'flex flex-row lg:col-start-24 lg:translate-y-[-210px] sm:pt-[8px] lg:pt-0 lg:translate-x-[16px]',
    phoneIcon: 'mt-3px',
    phoneLink: 'no-underline pl-5px text-grey100 hover:text-purple90',
    emailLink: 'pt-15px',
    websiteContent:
      'lg:col-start-24 lg:translate-y-[-200px] lg:translate-x-[16px] whitespace-normal sm:pt-[8px] lg:pt-0',
    googleMaps:
      'col-end-25 col-start-1 flex flex-1 h-[400px] mb-[100px] lg:mt-[-30px]',
  };

  const title = (
    <p className={className.title}>
      {name && !isSpecialCharacter(name) && name}
    </p>
  );

  const renderOpeningHoursItem = (item: SchemaTypes.OpeningHours) => (
    <p key={item.day} className={className.schedule}>
      <span className={className.workdays}>{weekdays[item.day ?? '']}</span>
      <span className={className.openingHoursRange}>
        {item.isActive
          ? translate(intl, 'ui.PDP.DEALER_INFO.OPENING_HOURS_RANGE', {
              from: item.from ?? '',
              to: item.to ?? '',
            })
          : translate(intl, 'ui.PDP.DEALER_INFO.OPENING_HOURS_CLOSED')}
      </span>
    </p>
  );

  return (
    <div className={className.wrapper}>
      <div className={className.description}>
        <div className={className.address}>
          {title}
          <p>
            {!isSpecialCharacter(street!) && street}{' '}
            {!isSpecialCharacter(houseNumber!) && houseNumber}
          </p>
          <p>{!isSpecialCharacter(additionalAddress!) && additionalAddress}</p>
          <p>
            {!isSpecialCharacter(zip!) && zip}{' '}
            {!isSpecialCharacter(city!) && city}
          </p>
          <p className={className.scheduleTitle}>
            {translate(
              intl,
              whitelabel
                ? 'ui.PDP.DEALER_INFO.CONTACT'
                : 'ui.PDP.DEALER_INFO.OPENING_HOURS_AND_CONTACT'
            )}
          </p>
        </div>
        {!whitelabel && (
          <div className={className.openingHours}>
            {retailOpeningHours?.length > 0 && (
              <div className={className.retailOpeningHours}>
                <p className={className.openingHoursSubtitle}>
                  {translate(intl, 'ui.PDP.DEALER_INFO.RETAIL_OPENING_HOURS')}
                </p>
                {retailOpeningHours
                  .filter((i) => i.day)
                  .map((item) => {
                    return renderOpeningHoursItem(item);
                  })}
                <div className={className.contactPersonContent}>
                  <div className={className.phoneContent}>
                    <div>
                      <Phone16 className={className.phoneIcon} />
                    </div>
                    <a href={`tel:${phone}`} className={className.phoneLink}>
                      {phone && !isSpecialCharacter(phone) && phone}
                    </a>
                  </div>
                  {website && (
                    <div className={className.websiteContent}>
                      <Button
                        text={translate(intl, 'ui.PDP.DEALER_INFO.WEBSITE')}
                        fullWidth={false}
                        onClick={() =>
                          window.open(`http://${website}`, '_blank')
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
            {serviceOpeningHours?.length > 0 && (
              <div className={className.serviceOpeningHours}>
                <p className={className.openingHoursSubtitle}>
                  {translate(intl, 'ui.PDP.DEALER_INFO.SERVICE_OPENING_HOURS')}
                </p>

                {serviceOpeningHours
                  .filter((i) => i.day)
                  .map((item) => {
                    return renderOpeningHoursItem(item);
                  })}
                <div className={className.contactPersonContent}>
                  <div className={className.phoneContent}>
                    <div>
                      <Phone16 className={className.phoneIcon} />
                    </div>
                    <a href={`tel:${phone}`} className={className.phoneLink}>
                      {phone && !isSpecialCharacter(phone) && phone}
                    </a>
                  </div>
                  {website && (
                    <div className={className.websiteContent}>
                      <Button
                        text={translate(intl, 'ui.PDP.DEALER_INFO.WEBSITE')}
                        fullWidth={false}
                        onClick={() =>
                          window.open(`http://${website}`, '_blank')
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <LocationMap
        lat={lat}
        lng={lon}
        width="100%"
        height="100%"
        className={className.googleMaps}
      />
    </div>
  );
};

export default DealerOverviewInfo;
